import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import Layout from '../../../components/Layout';
import BopBackLink from '../../../components/BopBackLink';
import ApiInspector from '../../../components/ApiInspector';
import { showToast } from '../../../components/Toast';
import { apiFetch } from '../../../lib/api';
import { usePermesso } from '../../../context/PermessiContext';
import { useDirtyForm } from '../../../hooks/useDirtyForm';
import { useBloccoRecordContesto } from '../../../hooks/useBloccoRecordContesto';
import TabClientiInsegna from './components/TabClientiInsegna';
import TabCodiciArticoloInsegna from './components/TabCodiciArticoloInsegna';
import TabContrattiInsegna from './components/TabContrattiInsegna';
import TabPremiInsegna from './components/TabPremiInsegna';
import { API_DOCS } from './InsegnaSchedaPage.api';
import { API_DOCS as API_DOCS_CLIENTI } from './components/TabClientiInsegna.api';
import { API_DOCS as API_DOCS_CODICI } from './components/TabCodiciArticoloInsegna.api';
import { API_DOCS as API_DOCS_CONTRATTI } from './components/TabContrattiInsegna.api';
import { API_DOCS as API_DOCS_PREMI } from './components/TabPremiInsegna.api';

const MODULO = 'anagrafiche.insegne';
const LISTA_PATH = '/anagrafiche/insegne';
const DOCS = [...API_DOCS, ...API_DOCS_CLIENTI, ...API_DOCS_CODICI, ...API_DOCS_CONTRATTI, ...API_DOCS_PREMI];

type Tab = 'dati' | 'clienti' | 'codici' | 'contratti' | 'premi';
const TABS: Tab[] = ['dati', 'clienti', 'codici', 'contratti', 'premi'];

interface Insegna {
  id: number;
  societaId: number;
  societa: { id: number; nome: string };
  codice: string;
  nome: string;
  note: string | null;
  attivo: boolean;
  _count: { clienti: number; codiciArticolo: number; contratti: number; premi: number };
}

interface FormDati {
  codice: string;
  nome: string;
  note: string;
}

const formDa = (i: Insegna): FormDati => ({ codice: i.codice, nome: i.nome, note: i.note || '' });

export default function InsegnaSchedaPage() {
  const { id } = useParams<{ id: string }>();
  const { can } = usePermesso();

  // La scheda attiva sta nell'URL: tornando indietro da un cliente si riapre la stessa
  const [searchParams, setSearchParams] = useSearchParams();
  const tabUrl = searchParams.get('tab') as Tab | null;

  const [insegna, setInsegna] = useState<Insegna | null>(null);
  const [loading, setLoading] = useState(true);
  const [errore, setErrore] = useState('');

  const [form, setForm] = useState<FormDati | null>(null);
  const [initialSnapshot, setInitialSnapshot] = useState('');
  const [saving, setSaving] = useState(false);
  const [cambioStato, setCambioStato] = useState(false);

  const canEdit = can(MODULO, 'edit', insegna?.societaId);
  const canDelete = can(MODULO, 'delete', insegna?.societaId);
  const canContratti = can('anagrafiche.insegne_contratti', 'view', insegna?.societaId);
  const canPremi = can('anagrafiche.insegne_premi', 'view', insegna?.societaId);

  const tabVisibile = (t: Tab) => (t === 'contratti' ? canContratti : t === 'premi' ? canPremi : true);
  const tab: Tab = tabUrl && TABS.includes(tabUrl) && tabVisibile(tabUrl) ? tabUrl : 'dati';
  const setTab = (t: Tab) => setSearchParams(t === 'dati' ? {} : { tab: t }, { replace: true });

  const isDirty = initialSnapshot !== '' && JSON.stringify(form) !== initialSnapshot;
  useDirtyForm(isDirty, `Insegna ${insegna?.nome ?? ''}`.trim());
  useBloccoRecordContesto({
    recordSocietaId: insegna?.societaId,
    label: insegna ? `Insegna ${insegna.nome}` : 'Insegna',
  });

  /** Ricarica l'insegna; con aggiornaForm=false tocca solo intestazione e conteggi, non i campi in modifica. */
  const carica = useCallback(async (aggiornaForm = true) => {
    try {
      // @api GET /api/anagrafiche/insegne/:id -> {"success":true,"data":{"id":3,"societaId":1,"societa":{"id":1,"nome":"Smartix"},"codice":"BRICO","nome":"Bricocenter","note":null,"attivo":true,"_count":{"clienti":2,"codiciArticolo":40,"contratti":1,"premi":3}}}
      const res = await apiFetch<{ success: boolean; data: Insegna }>(`/api/anagrafiche/insegne/${id}`);
      setInsegna(res.data);
      if (aggiornaForm) {
        const nuovo = formDa(res.data);
        setForm(nuovo);
        setInitialSnapshot(JSON.stringify(nuovo));
      }
    } catch (err: any) {
      const msg = err.message || "Errore nel caricamento dell'insegna";
      // Un aggiornamento dei soli conteggi che fallisce non deve far sparire la scheda
      if (aggiornaForm) setErrore(msg); else showToast(msg, 'error');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    setLoading(true);
    setErrore('');
    carica();
  }, [carica]);

  const aggiornaConteggi = useCallback(() => { void carica(false); }, [carica]);

  const salva = async () => {
    if (!form || !insegna) return;
    if (!form.nome.trim() || !form.codice.trim()) {
      showToast('Codice e nome sono obbligatori', 'error');
      return;
    }
    setSaving(true);
    try {
      // @api PUT /api/anagrafiche/insegne/:id {"codice":"BRICO","nome":"Bricocenter","note":null} -> {"id":3,"societaId":1,"codice":"BRICO","nome":"Bricocenter","note":null,"attivo":true}
      await apiFetch(`/api/anagrafiche/insegne/${insegna.id}`, {
        method: 'PUT',
        body: JSON.stringify({
          codice: form.codice.trim(),
          nome: form.nome.trim(),
          note: form.note.trim() || null,
        }),
      });
      setInitialSnapshot(JSON.stringify(form));
      showToast('Insegna salvata', 'success');
      await carica();
    } catch (err: any) {
      showToast(err.message || "Errore nel salvataggio dell'insegna", 'error');
    } finally {
      setSaving(false);
    }
  };

  const disattiva = async () => {
    if (!insegna) return;
    if (!window.confirm(
      `Disattivare l'insegna "${insegna.nome}"?\n\n` +
      'Non comparirà più nelle tendine di nominativi, contratti, premi e import ordini. ' +
      'Clienti, codici e ordini già collegati la mantengono. Si può riattivare in qualsiasi momento.'
    )) return;
    setCambioStato(true);
    try {
      // @api DELETE /api/anagrafiche/insegne/:id -> {"message":"Insegna disattivata"}
      await apiFetch(`/api/anagrafiche/insegne/${insegna.id}`, { method: 'DELETE' });
      showToast('Insegna disattivata', 'success');
      await carica(false);
    } catch (err: any) {
      showToast(err.message || "Errore nella disattivazione dell'insegna", 'error');
    } finally {
      setCambioStato(false);
    }
  };

  const riattiva = async () => {
    if (!insegna) return;
    setCambioStato(true);
    try {
      // @api POST /api/anagrafiche/insegne/:id/riattiva -> {"success":true}
      await apiFetch(`/api/anagrafiche/insegne/${insegna.id}/riattiva`, { method: 'POST' });
      showToast('Insegna riattivata', 'success');
      await carica(false);
    } catch (err: any) {
      showToast(err.message || "Errore nella riattivazione dell'insegna", 'error');
    } finally {
      setCambioStato(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="bop-insegna-scheda">
          <div className="bop-empty-state"><p className="bop-empty-state__text">Caricamento…</p></div>
        </div>
      </Layout>
    );
  }

  if (errore || !insegna || !form) {
    return (
      <Layout>
        <div className="bop-insegna-scheda">
          <BopBackLink to={LISTA_PATH} label="Torna alle insegne" />
          <div className="bop-card">
            <div className="bop-empty-state">
              <p className="bop-empty-state__text">{errore || 'Insegna non trovata'}</p>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="bop-insegna-scheda">
        <BopBackLink to={LISTA_PATH} label="Torna alle insegne" />

        <div className="bop-page-header">
          <div className="bop-page-header__text">
            <h2 className="bop-page-title">{insegna.nome}</h2>
            <div className="bop-insegna-scheda__sottotitolo">
              <span className="bop-badge bop-badge--info">{insegna.codice}</span>
              <span className="bop-text-muted">{insegna.societa.nome}</span>
              {!insegna.attivo && <span className="bop-badge bop-badge--neutral">Disattivata</span>}
            </div>
          </div>
          <div className="bop-insegna-scheda__header-actions">
            {insegna.attivo && canDelete && (
              <button className="bop-btn bop-btn-secondary" onClick={disattiva} disabled={cambioStato}>
                Disattiva
              </button>
            )}
            {!insegna.attivo && canEdit && (
              <button className="bop-btn bop-btn-secondary" onClick={riattiva} disabled={cambioStato}>
                Riattiva
              </button>
            )}
            {canEdit && tab === 'dati' && (
              <button className="bop-btn bop-btn-primary" onClick={salva} disabled={saving || !isDirty}>
                {saving ? 'Salvataggio…' : 'Salva'}
              </button>
            )}
          </div>
        </div>

        {!insegna.attivo && (
          <div className="bop-insegna-scheda__avviso">
            Insegna disattivata: non compare nelle tendine di nominativi, contratti, premi e import ordini.
          </div>
        )}

        <div className="bop-tabs bop-insegna-scheda__tabs">
          <button className={`bop-tab ${tab === 'dati' ? 'active' : ''}`} onClick={() => setTab('dati')}>
            Dati
          </button>
          <button className={`bop-tab ${tab === 'clienti' ? 'active' : ''}`} onClick={() => setTab('clienti')}>
            Clienti ({insegna._count.clienti})
          </button>
          <button className={`bop-tab ${tab === 'codici' ? 'active' : ''}`} onClick={() => setTab('codici')}>
            Codici articolo ({insegna._count.codiciArticolo})
          </button>
          {canContratti && (
            <button className={`bop-tab ${tab === 'contratti' ? 'active' : ''}`} onClick={() => setTab('contratti')}>
              Contratti quadro ({insegna._count.contratti})
            </button>
          )}
          {canPremi && (
            <button className={`bop-tab ${tab === 'premi' ? 'active' : ''}`} onClick={() => setTab('premi')}>
              Premi ({insegna._count.premi})
            </button>
          )}
        </div>

        {tab === 'dati' && (
          <div className="bop-card">
            <div className="bop-card__header">
              <h3 className="bop-card__title">Dati insegna</h3>
            </div>
            <div className="bop-card__body bop-insegna-scheda__form">
              <div className="bop-form-row">
                <div className="bop-form-group">
                  <label className="bop-label">Nome <span className="bop-text-danger">*</span></label>
                  <input
                    className="bop-input"
                    value={form.nome}
                    disabled={!canEdit}
                    placeholder="Es. Conad, Coop, Esselunga..."
                    onChange={(e) => setForm({ ...form, nome: e.target.value })}
                  />
                </div>
                <div className="bop-form-group">
                  <label className="bop-label">Codice <span className="bop-text-danger">*</span></label>
                  <input
                    className="bop-input"
                    value={form.codice}
                    disabled={!canEdit}
                    maxLength={20}
                    onChange={(e) => setForm({ ...form, codice: e.target.value.toUpperCase() })}
                  />
                  <span className="bop-insegne__hint">Codice univoco dell'insegna nella società</span>
                </div>
              </div>
              <div className="bop-form-group">
                <label className="bop-label">Note</label>
                <textarea
                  className="bop-input bop-insegne__textarea"
                  rows={4}
                  value={form.note}
                  disabled={!canEdit}
                  placeholder="Note aggiuntive..."
                  onChange={(e) => setForm({ ...form, note: e.target.value })}
                />
              </div>
            </div>
          </div>
        )}

        {tab === 'clienti' && <TabClientiInsegna insegnaId={insegna.id} />}

        {tab === 'codici' && (
          <TabCodiciArticoloInsegna
            insegnaId={insegna.id}
            canEdit={canEdit}
            canDelete={canDelete}
            onCambio={aggiornaConteggi}
          />
        )}

        {tab === 'contratti' && <TabContrattiInsegna insegnaId={insegna.id} />}

        {tab === 'premi' && <TabPremiInsegna insegnaId={insegna.id} />}
      </div>

      <ApiInspector docs={DOCS} />
    </Layout>
  );
}
